import { Divider, Form, Tag } from 'antd';
import AppTypography from '../../typography/Typography';
import { FIELD_REGISTRY, getFieldFormItemProps } from './fieldRegistry.jsx';
import { resolveOptions } from './utils/popupUtils';

/**
 * Renders the form section of DynamicPopup, including:
 *   - The "Already Assigned People" tag list (for others-assign)
 *   - All form fields via FIELD_REGISTRY
 *
 * @param {Object} props
 * @param {Object} props.form - Ant Design form instance
 * @param {Array} props.fields
 * @param {Object} props.initialValues
 * @param {Object} props.dynamicOptions
 * @param {Object} props.popupContext
 * @param {*} props.extraData
 * @param {string[]} props.removedIds
 * @param {Function} props.setRemovedIds
 * @param {boolean} [props.showAlreadyAssigned]
 * @param {Function} props.onValuesChange
 * @param {Function} props.getSearchHandler
 * @param {Function} props.getMentionSearchHandler
 * @param {Object} props.mentionOptions
 */
export default function PopupFields({
  form,
  fields,
  initialValues,
  dynamicOptions,
  popupContext,
  extraData,
  removedIds,
  setRemovedIds,
  showAlreadyAssigned,
  onValuesChange,
  getSearchHandler,
  getMentionSearchHandler,
  mentionOptions,
}) {
  const hasAssigned = showAlreadyAssigned && extraData;
  if (!fields.length && !hasAssigned) return null;

  return (
    <>
      {hasAssigned && (
        <>
          <div className="dynamic-popup__already-assigned">
            <AppTypography variant="caption" color="secondary">Already Assigned People</AppTypography>
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(extraData?.recruitersData ?? [])
                .filter((recruiter) => {
                  const id = String(
                    recruiter?.user_id ?? recruiter?.id ?? recruiter?.userId ?? '',
                  );
                  return id && !removedIds.includes(id);
                })
                .map((recruiter) => {
                  const id = String(
                    recruiter?.user_id ?? recruiter?.id ?? recruiter?.userId ?? '',
                  );
                  const name =
                    recruiter?.name ??
                    recruiter?.user_name ??
                    recruiter?.userName ??
                    recruiter?.email ??
                    id;
                  return (
                    <Tag
                      key={id || name}
                      closable
                      onClose={() => {
                        if (!id) return;
                        setRemovedIds((prev) => Array.from(new Set([...prev, id])));
                      }}
                    >
                      {name}
                    </Tag>
                  );
                })}
            </div>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {fields.length > 0 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onValuesChange={(_, allValues) => onValuesChange(allValues)}
        >
          {fields.map((field) => {
            const options = dynamicOptions[field.name] ?? resolveOptions(field, popupContext);
            const onSearch = getSearchHandler(field);
            const onMentionSearch = getMentionSearchHandler(field);
            const currentMentionOpts = mentionOptions[field.name] ?? [];
            const formItemProps = getFieldFormItemProps(field.type);
            const Control = FIELD_REGISTRY[field.type];

            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={
                  field.required
                    ? [{ required: true, message: `${field.label || 'This field'} is required` }]
                    : []
                }
                {...formItemProps}
              >
                {Control ? (
                  <Control
                    field={field}
                    options={options}
                    onSearch={onSearch}
                    mentionOpts={currentMentionOpts}
                    onMentionSearch={onMentionSearch}
                  />
                ) : null}
              </Form.Item>
            );
          })}
        </Form>
      )}
    </>
  );
}
